import {
  autoUpdate,
  computePosition,
  flip,
  hide,
  shift,
  type VirtualElement,
} from '@floating-ui/dom'
import { Editor, posToDOMRect, ReactRenderer } from '@tiptap/react'
import type { SuggestionOptions } from '@tiptap/suggestion'
import {
  MentionList,
  MentionListProps,
  type MentionItem,
  type MentionListHandle,
  type MentionableResource,
} from '.'

type MentionListRenderProps = {
  onMentionListOpenChange?: (open: boolean) => void
  parentEditor?: Editor
  /**
   * Stacking for the wrapper the list is mounted in. The list carries a z-index
   * of its own, which is only right for a surface that isn't stacked inside
   * anything; a surface that is gives the wrapper one that clears its own layers.
   */
  zIndex?: number
  /**
   * Set on the wrapper the list is mounted in. The list is portaled to the body,
   * out of reach of the surface that opened it — a marker attribute is how that
   * surface tells a click on a suggestion from a click away from itself.
   */
  attributes?: Record<string, string>
}

type MentionListRenderer = ReturnType<
  NonNullable<SuggestionOptions<MentionableResource, MentionItem>['render']>
>

export const createMentionListRenderer: (
  props?: MentionListRenderProps
) => () => MentionListRenderer =
  ({
    onMentionListOpenChange,
    parentEditor,
    zIndex,
    attributes,
  }: MentionListRenderProps = {}) =>
  () => {
    let autoUpdateCleanup: () => void
    let component:
      | ReactRenderer<MentionListHandle, MentionListProps>
      | undefined

    const handleOpenChange = (open: boolean) => {
      onMentionListOpenChange?.(open)
      if (component) {
        component.updateProps({ open })
        if (open && !component.element.isConnected) {
          document.body.appendChild(component.element)

          const element = component.element
          const reference = getVirtualElement(component.editor)
          autoUpdateCleanup = autoUpdate(reference, element, () => {
            updatePosition(reference, element, getBoundaryElement())
          })
        } else if (!open && component.element.isConnected) {
          component.element.remove()
          autoUpdateCleanup?.()
        }
      }
    }

    const getBoundaryElement = (): Element | undefined => {
      const editor = parentEditor || component?.editor
      if (!editor || !editor.isInitialized) {
        return
      }

      return editor?.view.dom.parentElement ?? undefined
    }

    const getVirtualElement = (editor: Editor): VirtualElement => {
      const { node: nodeDOM } = editor.view.domAtPos(
        editor.state.selection.from,
        -1
      )

      let contextElement: Element | undefined
      if (nodeDOM instanceof Text) {
        contextElement = nodeDOM.parentElement ?? undefined
      } else if (nodeDOM instanceof Element) {
        contextElement = nodeDOM
      }

      return {
        contextElement,
        getBoundingClientRect: () =>
          posToDOMRect(
            editor.view,
            editor.state.selection.from,
            editor.state.selection.to
          ),
      }
    }

    const updatePosition = async (
      reference: VirtualElement,
      element: HTMLElement,
      boundary: Element | undefined
    ) => {
      const { x, y, strategy, middlewareData } = await computePosition(
        reference,
        element,
        {
          placement: 'bottom-start',
          middleware: [
            shift(),
            flip({
              fallbackPlacements: ['top-start'],
              boundary,
            }),
            hide({ boundary }),
          ],
        }
      )

      element.style.width = 'max-content'
      element.style.position = strategy
      element.style.left = `${x}px`
      element.style.top = `${y}px`
      element.style.visibility = middlewareData.hide?.referenceHidden
        ? 'hidden'
        : 'visible'
    }

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props: {
            ...props,
            open: false,
            onOpenChange: handleOpenChange,
          },
          editor: props.editor,
        })

        if (!component) {
          return
        }

        const element = component.element
        element.style.position = 'absolute'
        if (zIndex !== undefined) element.style.zIndex = String(zIndex)
        Object.entries(attributes ?? {}).forEach(([name, value]) =>
          element.setAttribute(name, value)
        )
        handleOpenChange(true)
      },

      onUpdate(props) {
        if (!component?.element) {
          return
        }

        if (!component.props.open) {
          handleOpenChange(true)
        }

        component.updateProps(props)
        const reference = getVirtualElement(props.editor)
        updatePosition(reference, component.element, getBoundaryElement())
      },

      onKeyDown(props) {
        if (!component?.ref) {
          return false
        }

        return component.ref.onKeyDown(props) || false
      },

      onExit() {
        if (!component?.element) {
          return
        }

        handleOpenChange(false)
        component.destroy()
        component = undefined
      },
    }
  }
